import { GuitarMark } from '../GuitarMark.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf, EYEBROW, type ThemeClasses } from './cmsTheme.ts';
import type { FestivalData, Artist, DecoratedScheduleEntry, FestivalEvent, PageSectionTheme } from '../../lib/types.ts';
import { buildScheduleDayColumns, type ScheduleDayColumnData } from '../../domain/schedule.ts';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ScheduleSectionProps {
  data: FestivalData;
  onSelectArtist?: (artist: Artist) => void;
  /** Restrict to these day names (CMS-039 schedule section config). Absent/empty = all days. */
  restrictToDays?: readonly string[];
  /** Section theme; the day-column cards adopt the theme card shade. Defaults to 'ice'. */
  theme?: PageSectionTheme;
  /** Which event's days supply the open/close markers. Community day and Ung i Midten both fall on
   *  "Torsdag", so a day name alone is ambiguous — a section rendering one event's own feed must say
   *  which event it is. Absent = match by day name across every event (the mixed /program view). */
  eventType?: FestivalEvent['type'];
}

interface DayColumnProps {
  day: string;
  entries: readonly DecoratedScheduleEntry[];
  onSelectArtist: (artistId: string) => void;
  t: ThemeClasses;
}

// ---------------------------------------------------------------------------
// ScheduleSection
// ---------------------------------------------------------------------------

export function ScheduleSection({ data, onSelectArtist }: ScheduleSectionProps) {
  return (
    <section id="tidsplan" className="relative bg-mf-ice density-pad">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <ScheduleBlock data={data} onSelectArtist={onSelectArtist} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ScheduleBlock — a chronological per-day listing. One column per festival day,
// side by side on desktop and stacked on mobile; each day's acts are listed in
// time order with the stage shown as an attribute. Shared by ScheduleSection
// (bespoke /program wrapper) and CmsTimetableSection (CMS theme wrapper).
// ---------------------------------------------------------------------------

export function ScheduleBlock({ data, onSelectArtist, restrictToDays, theme = 'ice', eventType }: ScheduleSectionProps) {
  const columns = buildScheduleDayColumns(data, { restrictToDays, eventType });
  return <ScheduleColumnsBlock data={data} columns={columns} onSelectArtist={onSelectArtist} theme={theme} />;
}

/**
 * Renders precomputed day columns (possibly merged from several feeds, CMS-061/CMS-062) under
 * the shared heading. No columns = the "not ready yet" placeholder. Three or more columns gain
 * an extra grid column on desktop so e.g. Torsdag + the regular festival days sit side by side.
 */
export function ScheduleColumnsBlock({ data, columns, onSelectArtist, theme = 'ice' }: {
  data: FestivalData;
  columns: readonly ScheduleDayColumnData[];
  onSelectArtist?: (artist: Artist) => void;
  theme?: PageSectionTheme;
}) {
  const t = themeOf(theme);

  // Page-local broadcast so a co-located `artists` section can expand + scroll to this artist's
  // card, regardless of section order (CMS-034).
  const handleSelectArtist = (id: string) => {
    const artist = data.artistById[id];
    if (!artist) return;
    window.dispatchEvent(new CustomEvent('mf:select-artist', { detail: { artistId: artist.id } }));
    onSelectArtist?.(artist);
  };

  const gridCols = columns.length > 2
    ? 'md:grid-cols-2 lg:grid-cols-3'
    : columns.length > 1 ? 'md:grid-cols-2' : '';

  return (
    <>
      <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>
        Hvem spiller hvornår
      </SectionHeading>

      {columns.length === 0 ? (
        <EmptyState t={t} />
      ) : (
        <div className={`grid gap-6 md:gap-8 ${gridCols}`}>
          {columns.map(({ key, day, entries }) => (
            <DayColumn key={key} day={day} entries={entries} onSelectArtist={handleSelectArtist} t={t} />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DayColumn (internal) — one day's acts, chronological, stage as an attribute.
// ---------------------------------------------------------------------------

// Night-program items (00:00–04:59) belong to the previous day's late slot, so sort them last.
function sortKey(time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number);
  return (h < 5 ? h + 24 : h) * 60 + m;
}

function DayColumn({ day, entries, onSelectArtist, t }: DayColumnProps) {
  const sorted = entries.slice().sort((a, b) => sortKey(a.startTime) - sortKey(b.startTime));

  return (
    <div className={`rounded-card p-5 md:p-7 ${t.card.bg} ${t.card.text}`}>
      <div className={`font-display text-2xl leading-none mb-4 pb-3 border-b ${t.card.border}`}>
        {day}
      </div>
      <ul>
        {sorted.map(e => <ScheduleRow key={e.id} entry={e} onSelectArtist={onSelectArtist} t={t} />)}
      </ul>
    </div>
  );
}

// A single row. Time is the primary anchor — set at body size in semibold — because people scan
// the schedule by time first; the stage is a small accent label under the title.
function ScheduleRow({ entry: e, onSelectArtist, t }: { entry: DecoratedScheduleEntry; onSelectArtist: (id: string) => void; t: ThemeClasses }) {
  if (e.kind === 'marker') {
    return (
      <li className={`flex gap-4 py-3 border-t first:border-t-0 ${t.card.border}`}>
        <span className="shrink-0 w-16 md:w-20 text-right text-base font-semibold tabular-nums leading-tight">
          {e.startTime.slice(0, 5)}
        </span>
        <span className="flex-1 min-w-0 font-display text-lg md:text-xl leading-tight">{e.title}</span>
      </li>
    );
  }

  const body = (
    <>
      <span className="shrink-0 w-16 md:w-20 text-right">
        <span className="block text-base font-semibold tabular-nums leading-tight">{e.startTime.slice(0, 5)}</span>
        <span className={`block text-sm tabular-nums mt-0.5 ${t.card.muted}`}>{e.endTime.slice(0, 5)}</span>
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-display text-lg md:text-xl leading-tight">{e.title}</span>
        <span className={`mt-1 block ${EYEBROW} ${t.eyebrow}`}>{e.stageName}</span>
        {e.buyUrl && (
          <a
            href={e.buyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-2 inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 ${t.eyebrow}`}
          >
            Køb billet
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
          </a>
        )}
      </span>
    </>
  );

  return (
    <li className={`border-t first:border-t-0 ${t.card.border}`}>
      {e.buyUrl ? (
        <div className="flex gap-4 py-3">{body}</div>
      ) : (
        <button
          onClick={() => onSelectArtist(e.artistId)}
          className="w-full flex gap-4 py-3 text-left transition-opacity hover:opacity-70"
        >
          {body}
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// EmptyState (internal)
// ---------------------------------------------------------------------------

// Generic, hard-coded placeholder shown whenever a schedule has no entries yet. Deliberately
// mentions no specific day or feed (festival / Community day / Ung i Midten) so it reads the same
// everywhere.
function EmptyState({ t }: { t: ThemeClasses }) {
  return (
    <div className={`relative overflow-hidden rounded-card p-10 md:p-16 ${t.card.bg} ${t.card.text}`}>
      {/* Decorative guitar pinned to the lower-left, spanning the full height of the card. */}
      <div className="absolute left-0 bottom-0 h-full opacity-10 pointer-events-none" aria-hidden>
        <GuitarMark className="h-full w-auto" />
      </div>
      <div className="relative text-center">
        <div className="font-display text-3xl md:text-[40px] leading-none tracking-[-0.02em]">
          Tidsplanen er ikke klar endnu
        </div>
        <p className={`mt-3 max-w-md mx-auto font-normal ${t.card.muted}`}>
          Vi arbejder på højtryk for at få alle spilletider på plads. Kig forbi igen om lidt – så er den klar.
        </p>
      </div>
    </div>
  );
}
